import { useEffect } from 'react'

interface UseGlobalHotkeyProps {
    key: string
    onKeyDown: () => void
    enabled?: boolean
    modifier?: 'mod' | 'ctrl' | 'meta' | 'none'
}

export function useGlobalHotkey({
    key,
    onKeyDown,
    enabled = true,
    modifier = 'none',
}: UseGlobalHotkeyProps) {
    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return

        function handleKeyDown(event: KeyboardEvent) {
            const matchesKey = event.key.toLowerCase() === key.toLowerCase()
            if (!matchesKey || !matchesModifier(event, modifier)) return

            event.preventDefault()
            onKeyDown()
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [enabled, key, modifier, onKeyDown])
}

function matchesModifier(
    event: KeyboardEvent,
    modifier: UseGlobalHotkeyProps['modifier'],
): boolean {
    switch (modifier) {
        case 'mod':
            return event.metaKey || event.ctrlKey
        case 'ctrl':
            return event.ctrlKey
        case 'meta':
            return event.metaKey
        case 'none':
        case undefined:
            return true
        default:
            return assertExhaustive(modifier)
    }
}

function assertExhaustive(value: never): never {
    throw new Error(`Unhandled hotkey modifier: ${String(value)}`)
}
